import { ExternalLink } from "lucide-react";
import navSkullZebra from "@/assets/nav-skull-zebra.png";
import deadWordsLogo from "@/assets/dead-words-logo.png";
import deadAirLogo from "@/assets/dead-air-logo.png";
import deadMastersLogo from "@/assets/dead-masters-logo.png";

export default function Tools() {
  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <img 
              src={navSkullZebra} 
              alt="Tools" 
              className="w-24 h-24 md:w-32 md:h-32 object-contain"
            />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#e0e0e0] mb-4 font-holly-berry">
            I'm sure you're just dying to try our premium tools
          </h1>
          <p className="text-lg text-[#909090] max-w-2xl mx-auto">
            Powerful tools to help you create, promote, and share your music across the internet.
          </p>
        </div>

        {/* Tools Grid */}
        <div className="grid gap-8 md:gap-10">

          {/* Dead Words Tool */}
          <a
            href="https://deadwords.us"
            target="_blank"
            rel="noopener noreferrer"
            className="group block"
          >
            <div 
              className="relative bg-[#1a1a1a] border border-[#2a2a2a] rounded-3xl p-8 md:p-10 transition-all duration-300 hover:scale-[1.02] hover:border-[#3a3a3a]"
              style={{
                boxShadow: `0 0 0 rgba(0,0,0,0)`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = `0 0 40px rgba(168, 85, 247, 0.4)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = `0 0 0 rgba(0,0,0,0)`;
              }}
            >
              {/* Dead Words Title */}
              <div className="flex items-center gap-4 md:gap-6 mb-6">
                <img 
                  src={deadWordsLogo} 
                  alt="Dead Words" 
                  className="w-24 h-24 md:w-32 md:h-32 object-contain"
                />
                <h2 className="text-3xl md:text-5xl font-bold text-[#e0e0e0] font-holly-berry tracking-wide">
                  Dead Words
                </h2>
              </div>

              {/* Content */}
              <h3 className="text-2xl md:text-3xl font-bold text-[#e0e0e0] mb-4 group-hover:text-white transition-colors">
                Lyric Video Generation Tool
              </h3>
              <p className="text-[#a0a0a0] text-lg mb-6 leading-relaxed">
                Automatically turn your song and images into a slideshow lyric video that is perfectly timed.
              </p>

              {/* Link indicator */}
              <div className="flex items-center gap-2 text-[#808080] group-hover:text-[#d0d0d0] transition-colors">
                <span className="text-sm font-medium">deadwords.us</span>
                <ExternalLink className="w-4 h-4" />
              </div>
            </div>
          </a>

          {/* Dead Air Tool */}
          <a
            href="https://deadair.us"
            target="_blank"
            rel="noopener noreferrer"
            className="group block"
          >
            <div 
              className="relative bg-[#1a1a1a] border border-[#2a2a2a] rounded-3xl p-8 md:p-10 transition-all duration-300 hover:scale-[1.02] hover:border-[#3a3a3a]"
              style={{
                boxShadow: `0 0 0 rgba(0,0,0,0)`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = `0 0 40px rgba(34, 211, 238, 0.4)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = `0 0 0 rgba(0,0,0,0)`;
              }}
            >
              {/* Dead Air Title */}
              <div className="flex items-center gap-4 md:gap-6 mb-6">
                <img 
                  src={deadAirLogo} 
                  alt="Dead Air" 
                  className="w-16 h-16 md:w-24 md:h-24 object-contain"
                />
                <h2 className="text-3xl md:text-5xl font-bold text-[#e0e0e0] font-holly-berry tracking-wide">
                  Dead Air
                </h2>
              </div>

              {/* Content */}
              <h3 className="text-2xl md:text-3xl font-bold text-[#e0e0e0] mb-4 group-hover:text-white transition-colors">
                Crosspost to ANY Platform
              </h3>
              <p className="text-[#a0a0a0] text-lg mb-6 leading-relaxed">
                With our artists' favorite crossposting tool, you can post to all your socials with the click of a single button. No more spending hours trying to repost your content.
              </p>

              {/* Link indicator */}
              <div className="flex items-center gap-2 text-[#808080] group-hover:text-[#d0d0d0] transition-colors">
                <span className="text-sm font-medium">deadair.us</span>
                <ExternalLink className="w-4 h-4" />
              </div>
            </div>
          </a>

          {/* Dead Masters Tool */}
          <a
            href="https://deadmasters.us"
            target="_blank"
            rel="noopener noreferrer"
            className="group block"
          >
            <div 
              className="relative bg-[#1a1a1a] border border-[#2a2a2a] rounded-3xl p-8 md:p-10 transition-all duration-300 hover:scale-[1.02] hover:border-[#3a3a3a]"
              style={{
                boxShadow: `0 0 0 rgba(0,0,0,0)`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = `0 0 40px rgba(168, 85, 247, 0.4)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = `0 0 0 rgba(0,0,0,0)`;
              }}
            >
              {/* Dead Masters Title */}
              <div className="flex items-center gap-4 md:gap-6 mb-6">
                <img 
                  src={deadMastersLogo} 
                  alt="Dead Masters" 
                  className="w-16 h-16 md:w-24 md:h-24 object-contain"
                />
                <h2 className="text-3xl md:text-5xl font-bold text-[#e0e0e0] font-holly-berry tracking-wide">
                  Dead Masters
                </h2>
              </div>

              {/* Content */}
              <h3 className="text-2xl md:text-3xl font-bold text-[#e0e0e0] mb-4 group-hover:text-white transition-colors">
                Professional Mastering Tool
              </h3>
              <p className="text-[#a0a0a0] text-lg mb-6 leading-relaxed">
                Upload your track and receive a fully high-quality mastered version. Professional sound without the professional price tag.
              </p>

              {/* Link indicator */}
              <div className="flex items-center gap-2 text-[#808080] group-hover:text-[#d0d0d0] transition-colors">
                <span className="text-sm font-medium">deadmasters.us</span>
                <ExternalLink className="w-4 h-4" />
              </div>
            </div>
          </a>
        </div>

        {/* Footer note */}
        <div className="text-center mt-12 text-[#606060] text-sm">
          <p>More tools coming soon...</p>
        </div>
      </div>
    </div>
  );
}
